import * as React from "react";
import {
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import AddLabValues from "../patient_reports/health_overview/panels/AddLabValues";
import AddData from "../patient-dashboard/AddData";
import moment from "moment/moment";
import { useFlash } from "../FlashContext";

interface Props {
  readOnly: boolean;
  a1cValue: string;
  setA1cValue: any;
  bloodPressureValue: string;
  setBloodPressureValue: any;
  appoimentType: string;
  setAppoimentType: any;
  patientId: string;
  consentAcknowledge: boolean;
  setConsentAcknowledge: any;
}

const ClinicalDataPanel: React.FC<Props> = (props: any) => {
  const authSettings = React.useContext(AuthenticationContext);
  const [a1cOptions, setA1cOptions] = React.useState<any>([]);
  const [bloodPressureOptions, setBloodPressureOptions] = React.useState<any>(
    []
  );
  const [openA1cModal, setOpenA1cModal] = React.useState(false);
  const [addData, setAddData] = React.useState<any>(null);
  const { showToast } = useFlash();
  const [openBloodPressureModal, setOpenBloodPressureModal] =
    React.useState(false);

  React.useEffect(() => {
    getA1cOptions();
    getBloodPressureOptions();
  }, []);

  const getA1cOptions = () => {
    fetch(`/data_fetching/get_labs/${props.patientId}`, {
      method: "GET",
      headers: getHeaders(authSettings.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        setA1cOptions(result?.resource?.hgb_data_values?.slice(0, 7));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getBloodPressureOptions = () => {
    fetch(`/data_fetching/my_data/${props.patientId}`, {
      method: "GET",
      headers: getHeaders(authSettings.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (typeof result.error !== "undefined") {
          showToast({ title: "Error", text: result.error, type: "error" });
        } else {
          setBloodPressureOptions(result?.blood_pressure_readings?.slice(0, 7));
        }
      })
      .catch((error) => {
        showToast({ title: "Error", text: "Failed to load blood pressure data.", type: "error" });
      });
  };

  const selectA1cOptions = a1cOptions.map((a1c) => (
    <MenuItem key={a1c.id} value={a1c.id.toString()}>
      {`${a1c.reading_value} - ${moment(a1c.date_recorded).format(
        "MM/DD/YYYY"
      )}`}
    </MenuItem>
  ));

  const selectBloodPressureOptions = bloodPressureOptions.map(
    (bloodPRessure) => (
      <MenuItem key={bloodPRessure.id} value={bloodPRessure.id.toString()}>
        {`${bloodPRessure.sys_dis_value} - ${moment(
          bloodPRessure.date_recorded
        ).format("MM/DD/YYYY")}`}
      </MenuItem>
    )
  );

  return (
    <Grid container className="clinical-data-panel">
      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className="admin-header"
        >
          <h3 style={{ display: "inline-block" }}>
            Clinical Data For Encounter
          </h3>
          {!props.readOnly && (
            <div
              className="action-container"
              style={{ display: "inline-block" }}
            >
              <Link
                className="action-link"
                sx={{ marginRight: "18px" }}
                onClick={() => setOpenA1cModal(true)}
              >
                <AddIcon className="plus-icon" />{" "}
                <span className="">Add New A1C Value</span>
              </Link>
              <Link
                className="action-link"
                onClick={() => setAddData("blood_pressure")}
              >
                <AddIcon className="plus-icon" />{" "}
                <span className="">Add New Blood Pressure Value</span>
              </Link>
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container item className="form-body" spacing={1} xs={12}>
        <Grid container item xs={3}>
          <Grid item xs={12}>
            <InputLabel htmlFor="ecounter_a1c" className="field-label">
              Select Encounter A1C Value
            </InputLabel>
          </Grid>
          <Grid
            item
            xs={12}
            className="field-container"
            style={{ marginRight: "0px" }}
          >
            <TextField
              id="ecounter_a1c"
              size="small"
              value={props.a1cValue || "Select A1C Value"}
              className="field field-1"
              variant="outlined"
              onChange={(event) => props.setA1cValue(event.target.value)}
              select
              disabled={props.readOnly}
            >
              <MenuItem value="Select A1C Value" disabled>
                Select A1C Value
              </MenuItem>
              {a1cOptions && selectA1cOptions}
            </TextField>
          </Grid>
        </Grid>
        <Grid container item xs={3}>
          <Grid item xs={12}>
            <InputLabel
              htmlFor="encounter_blood_pressure"
              className="field-label"
            >
              Select Encounter Blood Pressure Value
            </InputLabel>
          </Grid>
          <Grid
            item
            xs={12}
            className="field-container"
            style={{ marginRight: "0px" }}
          >
            <TextField
              id="encounter_blood_pressure"
              size="small"
              value={props.bloodPressureValue || "Select Blood Pressure Value"}
              className="field field-1"
              variant="outlined"
              onChange={(event) =>
                props.setBloodPressureValue(event.target.value)
              }
              select
              disabled={props.readOnly}
            >
              <MenuItem value="Select Blood Pressure Value" disabled>
                Select Blood Pressure Value
              </MenuItem>
              {bloodPressureOptions && selectBloodPressureOptions}
            </TextField>
          </Grid>
        </Grid>
        <Grid container item xs={2.5}>
          <Grid item xs={12}>
            <InputLabel className="field-label">Appointment Type</InputLabel>
          </Grid>
          <Grid item xs={12}>
            <div style={{ display: "inline-block" }}>
              <Radio
                checked={props.appoimentType === "initial_visit"}
                onChange={() => props.setAppoimentType("initial_visit")}
                value="initial_visit"
                name="radio-buttons"
                inputProps={{ "aria-label": "Initial Visit" }}
                style={{ backgroundColor: "transparent", paddingLeft: 0 }}
                disabled={props.readOnly}
              />
              <span className="radio-text">Initial Visit</span>
            </div>
            <div style={{ display: "inline-block" }}>
              <Radio
                checked={props.appoimentType === "follow_up"}
                onChange={() => props.setAppoimentType("follow_up")}
                value="follow_up"
                name="radio-buttons"
                inputProps={{ "aria-label": "Follow-Up" }}
                style={{ backgroundColor: "transparent" }}
                disabled={props.readOnly}
              />
              <span className="radio-text">Follow-Up</span>
            </div>
          </Grid>
        </Grid>
        <Grid container item xs={3.5}>
          <Grid item xs={12}>
            <InputLabel className="field-label">
              Consent Acknowledgment
            </InputLabel>
          </Grid>
          <Grid item xs={12} style={{ marginTop: "5px", marginBottom: "5px" }}>
            <FormControlLabel
              value="end"
              control={
                <Checkbox
                  checked={props.consentAcknowledge}
                  onChange={(e) =>
                    props.setConsentAcknowledge(e.target.checked)
                  }
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "22px",
                    letterSpacing: "-0.1px",
                    color: "#1E1E1E",
                  }}
                >
                  I confirm that this patient has been informed of their
                  enrollment in the NDPERS program and has given verbal/written
                  consent.
                </Typography>
              }
              labelPlacement="end"
            />
          </Grid>
        </Grid>
      </Grid>
      {open && (
        <AddLabValues
          patient_id={props.patientId}
          open={openA1cModal}
          setOpenAddModal={setOpenA1cModal}
          onSuccess={(a1cValue) => {
            getA1cOptions();
            props.setA1cValue(a1cValue);
          }}
          onModalClose={() => {}}
          labObject={null}
          selectedTestType="a1c"
        />
      )}
      {addData && (
        <AddData
          patient_id={props.patientId}
          button_src={props.button_src}
          csrfToken={authSettings.csrfToken}
          setAddData={setAddData}
          addData={addData}
          editData={null}
          setEditData={() => {}}
          setShowNotes={() => {}}
          source={"provider"}
          type={"blood_pressure"}
          onSuccess={(bloodPressureValue) => {
            getBloodPressureOptions();
            props.setBloodPressureValue(bloodPressureValue);
          }}
        />
      )}
    </Grid>
  );
};

export default ClinicalDataPanel;
